// Serial action executor for Copilot.
// Drives injectAction for each action returned by /turn, accumulating
// structured per-action results for the next turn's tool_results.
// Per spec § 6.1 action execution semantics.

import { injectAction, waitForFramebufferSettle, captureZoomRegion } from "./guacamoleHook";
import { blobToDataUrl } from "./blobCodec";
import {
  ensureOverlay,
  drawCircle,
  drawArrow,
  drawRectangle,
  clearAnnotations,
  showClickMarker,
  showDragMarker,
  showScrollMarker,
  showKeyboardMarker,
  glideAgentCursorTo,
  dismissAgentCursor,
  AGENT_CURSOR_DRAG_MS,
} from "./annotationOverlay";

const CONTROL_ACTIONS = new Set([
  "left_click", "right_click", "middle_click", "double_click", "triple_click",
  "left_click_drag", "type", "type_at", "key", "scroll", "mouse_move",
  "left_mouse_down", "left_mouse_up", "hold_key",
]);
// Deliberately excluded from CONTROL_ACTIONS:
//   - "screenshot": observation only.
//   - "show_on_screen": browser-local overlay.
//   - "wait": pure delay. (Spec § 4.2 + § 10.1.)

// Control actions that target a coordinate get the agent cursor glided to that
// point before injection. type/key have no coordinate, so the cursor parks.
const CURSOR_GLIDE_ACTIONS = new Set([
  "left_click", "right_click", "middle_click", "double_click", "triple_click",
  "left_click_drag", "scroll", "mouse_move", "type_at",
]);

function _glideTarget(a) {
  if (a.type === "left_click_drag") return a.start_coordinate || [0, 0];
  return a.coordinate || [0, 0];
}

// Validate model-space coordinates/regions against the scaled screenshot
// bounds before we inject. Returns an error string or null.
function _outOfBounds(a, scaling) {
  if (!scaling) return null;
  const W = scaling.scaledWidth, H = scaling.scaledHeight;
  const inB = ([x, y]) => x >= 0 && y >= 0 && x <= W && y <= H;
  for (const c of [a.coordinate, a.start_coordinate]) {
    if (c && !inB(c)) return `coordinate_out_of_bounds:[${c}] vs [${W},${H}]`;
  }
  if (a.region) {
    const [x0, y0, x1, y1] = a.region;
    if (!inB([x0, y0]) || !inB([x1, y1]) || x1 <= x0 || y1 <= y0) {
      return `coordinate_out_of_bounds:region[${a.region}] vs [${W},${H}]`;
    }
  }
  return null;
}

/**
 * Execute a single action. Returns a human-readable summary string.
 * Throws on Guacamole-level failure so the caller can catch and record an error result.
 *
 * @param {HTMLIFrameElement} iframe
 * @param {{ factorX: number, factorY: number }} scaling - Result of getScale().
 * @param {object} action
 * @returns {Promise<string>} summary text
 */
// Per-type structured result text. The model reads this in
// `recent_action_summary` and uses it to ground its own next decision.
// Keep these mechanical and short — coords, char counts, chord names —
// no editorialisation.
function _summarize(action) {
  switch (action.type) {
    case "left_click":
    case "right_click":
    case "middle_click":
    case "double_click":
    case "triple_click":
    case "mouse_move": {
      const [x, y] = action.coordinate || [0, 0];
      return `${action.type}@[${x},${y}]`;
    }
    case "left_click_drag": {
      const [sx, sy] = action.start_coordinate || [0, 0];
      const [ex, ey] = action.coordinate || [0, 0];
      return `drag@[${sx},${sy}]->[${ex},${ey}]`;
    }
    case "type": {
      const text = action.text || "";
      const tail = text.endsWith("\n") ? " (ends with newline)" : "";
      return `typed ${text.length} chars${tail}`;
    }
    case "type_at": {
      const [x, y] = action.coordinate || [0, 0];
      const text = action.text || "";
      const enter = action.press_enter ? " +Enter" : "";
      const clear = action.clear_before_typing ? " (cleared first)" : "";
      return `type_at@[${x},${y}] ${text.length} chars${enter}${clear}`;
    }
    case "key":
      return `key ${action.text || ""}`;
    case "scroll": {
      const dir = action.scroll_direction || "?";
      const amt = action.scroll_amount || 1;
      const [x, y] = action.coordinate || [0, 0];
      return `scroll ${dir} ${amt} @[${x},${y}]`;
    }
    case "wait":
      return `waited ${action.duration ?? 1}s`;
    case "show_on_screen": {
      const kind = action.kind || "circle";
      const [x, y] = action.coordinate || [0, 0];
      return `show_on_screen ${kind}@[${x},${y}]`;
    }
    case "left_mouse_down":
    case "left_mouse_up": {
      const [x, y] = action.coordinate || [0, 0];
      return `${action.type}@[${x},${y}]`;
    }
    case "hold_key":
      return `hold_key ${action.text || ""} ${action.duration ?? 1}s`;
    default:
      return `${action.type} executed`;
  }
}

async function executeSingleAction(iframe, scaling, action) {
  const out = await injectAction(iframe, action, scaling);
  if (!out.ok) {
    throw new Error(out.error || "injectAction returned ok:false");
  }
  return _summarize(action);
}

/**
 * Execute a list of actions returned by a /turn response.
 *
 * Actions are executed serially in array order. On the first failure the
 * executor halts and marks remaining actions `halted_after_prior_failure`.
 *
 * When an action has a safety_check field:
 *   1. Pause and surface a confirmation card (via confirmFn).
 *   2. If denied: record user_denied_safety_check and halt.
 *   3. If allowed: inject the action and record text result.
 *
 * After each executed action the framebuffer is allowed to settle
 * (waitForFramebufferSettle) so the next action acts on a stable screen and,
 * once the batch finishes, the controller captures the settled frame as the
 * model's next observation. The model never requests a screenshot — a stray
 * `screenshot` action is injected as a harmless no-op.
 *
 * @param {HTMLIFrameElement} iframe - The active VM iframe (passed to guacamoleHook).
 * @param {{ scaledWidth: number, scaledHeight: number, factorX: number, factorY: number }} scaling
 *   Result of getScale(). captureScreenshot reads scaledWidth/scaledHeight.
 * @param {Array<object>} actions - Action list from /turn response body.actions.
 * @param {function(action): Promise<"allowed"|"denied">} confirmFn
 *   Called when action.safety_check is present.
 * @returns {Promise<Array<{id: string, text?: string, error?: string, image_data_url?: string}>>}
 */
export async function executeActions(
  iframe, scaling, actions,
  confirmFn,
  permissionFn,
  permissionStateRef,
) {
  permissionFn = permissionFn || (async () => "granted");
  permissionStateRef = permissionStateRef || {
    get: () => "granted", set: () => {}, remembered: false,
  };
  // Tier 1.c § 4.1 — ensure the overlay is mounted into the canvas's
  // own document. ensureOverlay is idempotent; calling it at the top
  // of every executeActions call is cheap and avoids new controller
  // lifecycle hooks for the Guacamole canvas.
  //
  // Pass the SCALED screenshot dimensions as the overlay's coordSpace —
  // the model emits annotation/click coordinates against the scaled
  // screenshot (capped at MAX_LONG_EDGE), not the native canvas pixels.
  // Without this the overlay viewBox = native pixels and circles render
  // offset by the downscale factor.
  try {
    const canvas = iframe?.contentDocument?.querySelector?.("canvas");
    if (canvas) {
      ensureOverlay(canvas, scaling
        ? { width: scaling.scaledWidth, height: scaling.scaledHeight }
        : undefined);
    }
  } catch (_) {
    // Overlay unavailable — annotation calls will silently no-op.
  }
  const results = [];
  let halted = false;
  for (const a of actions) {
    if (halted) {
      results.push({ id: a.id, error: "halted_after_prior_failure" });
      continue;
    }
    // Any non-annotation action supersedes whatever the model was pointing
    // at — screenshots, clicks, typing, scrolling, permission requests.
    // show_on_screen handles its own overlay state below (draws stack;
    // kind:"clear" wipes).
    if (a.type !== "show_on_screen") {
      clearAnnotations();
    }
    if (a.type !== "show_on_screen") {
      const oob = _outOfBounds(a, scaling);
      if (oob) {
        results.push({ id: a.id, error: oob });
        continue;  // self-correctable — do not halt
      }
    }
    if (a.type === "show_on_screen") {
      // The agent switched to guide-pointing — dismiss the drive cursor (keep
      // its position so a later mouse action resumes with a glide).
      dismissAgentCursor();
      const kind = a.kind || "circle";
      const [x, y] = a.coordinate || [0, 0];
      let status;
      try {
        if (kind === "clear") {
          clearAnnotations();
          status = "cleared annotations";
        } else if (kind === "circle") {
          drawCircle(x, y, a.label || "", a.radius || 36);
          status = "circled";
        } else if (kind === "arrow") {
          drawArrow(x, y, a.label || "");
          status = "arrow drawn";
        } else if (kind === "rectangle") {
          const [w, h] = a.size || [200, 100];
          drawRectangle(x, y, w, h, a.label || "");
          status = "boxed";
        } else {
          status = `unsupported annotation kind: ${kind}`;
        }
        results.push({ id: a.id, text: status });
      } catch (err) {
        results.push({ id: a.id, error: `annotation_failed: ${err?.message || String(err)}` });
      }
      continue;  // never halts
    }
    if (a.type === "request_permission") {
      // Model is asking explicitly — render the permission card with the
      // model's task_hint, then record the decision in permission state
      // so subsequent control actions don't re-prompt. Sticky 'remembered'
      // behaviour is intentionally left to the auto-flow path; explicit
      // requests treat the decision as a one-task answer.
      const decision = await permissionFn(a.task_hint);
      permissionStateRef.set(decision);
      results.push({ id: a.id, text: `decision: ${decision}` });
      continue;  // never halts — denial just means the model proceeds in Guide
    }
    if (a.type === "zoom") {
      // Observation-only: crop the already-settled frame and return it as an
      // image result. No input injection, no control permission, no settle.
      try {
        const blob = await captureZoomRegion(iframe, a.region || [0, 0, 0, 0], scaling);
        const image_data_url = await blobToDataUrl(blob);
        results.push({
          id: a.id, type: "zoom", image_data_url, media_type: "image/jpeg",
          text: `zoom region [${(a.region || []).join(",")}]`,
        });
      } catch (err) {
        results.push({ id: a.id, error: `zoom_failed: ${err?.message || String(err)}` });
      }
      continue;  // never halts; no framebuffer settle
    }
    if (CONTROL_ACTIONS.has(a.type)) {
      if (permissionStateRef.remembered && permissionStateRef.get() === "ungranted") {
        permissionStateRef.set("granted");
      }
      if (permissionStateRef.get() === "ungranted") {
        const decision = await permissionFn();
        permissionStateRef.set(decision);
      }
      if (permissionStateRef.get() === "denied") {
        results.push({
          id: a.id,
          error: "user_declined_control",
          text: "User declined to grant control of the desktop. Switch to Guide Mode: "
              + "use the show_on_screen tool (circle / arrow / rectangle) to point at "
              + "what they should click, and explain what to do in 1-2 short sentences. "
              + "Your previous <plan> was Drive-flavoured ('click X, type Y') and no "
              + "longer applies — emit a FRESH <plan> block describing the Guide-mode "
              + "steps (point/explain/wait) BEFORE your first show_on_screen, and "
              + "reset 'Current step: 1/N' accordingly. "
              + "Do not call computer actions for this task.",
        });
        halted = true;
        continue;
      }
    }
    try {
      if (a.safety_check) {
        const decision = await confirmFn(a);
        if (decision !== "allowed") {
          results.push({ id: a.id, error: "user_denied_safety_check" });
          halted = true;
          continue;
        }
      }
      // Telegraph intent: glide the synthetic cursor to the target and wait for
      // it to arrive before the real input fires. Pure UX; never affects status.
      if (CURSOR_GLIDE_ACTIONS.has(a.type)) {
        const [gx, gy] = _glideTarget(a);
        await glideAgentCursorTo(gx, gy);
      }
      // Tier 1.c § 4.2 — paint click visualisation marker. Pure UX; never
      // affects the action's success/failure status.
      if (a.type === "left_click_drag") {
        const [x1, y1] = a.start_coordinate || [0, 0];
        const [x2, y2] = a.coordinate || [0, 0];
        // The cursor already glided to the start (above). Draw the slower drag
        // line and have the cursor FOLLOW it from start to end, in sync, before
        // the real drag injects.
        showDragMarker(x1, y1, x2, y2, AGENT_CURSOR_DRAG_MS);
        await glideAgentCursorTo(x2, y2, AGENT_CURSOR_DRAG_MS);
      } else if (a.type === "scroll") {
        showScrollMarker(a.coordinate?.[0] || 0, a.coordinate?.[1] || 0,
                         a.scroll_direction || "down");
      } else if (a.type === "type" || a.type === "key" || a.type === "hold_key") {
        // Keyboard action (non-mouse) — dismiss the drive cursor (keep its
        // position); the keyboard pill conveys what's being typed.
        dismissAgentCursor();
        showKeyboardMarker(a.text || "", a.type === "key" || a.type === "hold_key");
      } else if (CONTROL_ACTIONS.has(a.type)) {
        const [x, y] = a.coordinate || [0, 0];
        showClickMarker(x, y, a.type);
      }
      const summary = await executeSingleAction(iframe, scaling, a);
      results.push({ id: a.id, text: summary });
      // Let the remote screen settle before the next action fires (and, for
      // the last action, before the controller captures the observation).
      await waitForFramebufferSettle(iframe);
    } catch (err) {
      results.push({ id: a.id, error: `action_failed: ${err?.message || String(err)}` });
      halted = true;
    }
  }
  return results;
}
